import { AgentConfig } from "../types";
import { logger } from '@/utils/logger';
import systemPrompt from '@agents/publishing/prompt.md?raw';

import { AutonomousIntelligence } from '@/services/intelligence/AutonomousIntelligence';
import type { Schema } from '@/shared/types/ai.dto';
import { PublishingTools } from '../tools/PublishingTools';
import { CatalogAdminTools } from '../tools/CatalogAdminTools';
import { UniversalTools } from '../tools/UniversalTools';
import { buildDomainRetrievalTools, buildDomainRetrievalDeclarations } from '../tools/DomainTools';



const publishingRetrievalConfig = {
    'publishingCatalog': { path: 'publishingCatalog', requiresUserIdFilter: true },
    'iswc_works': { path: 'iswc_works', requiresUserIdFilter: true },
    'publishing_registrations': { path: 'publishing_registrations', requiresUserIdFilter: true }
};
const publishingRetrievalTools = buildDomainRetrievalTools('Publishing', publishingRetrievalConfig);
const publishingRetrievalDeclarations = buildDomainRetrievalDeclarations('Publishing', publishingRetrievalConfig);

export const PublishingAgent: AgentConfig = {
    id: 'publishing',
    name: 'Publishing Director',
    description: 'Manages musical rights, royalties, and catalog administration.',
    color: 'bg-indigo-600',
    category: 'department',
    systemPrompt: systemPrompt,
    functions: {
        ...publishingRetrievalTools,
        register_work: async (args: { title: string, writers: string[], split: string }) => {
            const prompt = `Validate this music work registration draft. Title: "${args.title}", Contributors: ${args.writers.join(', ')}. Return missing fields, split warnings, and filing readiness. Do not generate or claim an official ISWC.`;
            try {
                const response = await AutonomousIntelligence.generateStructuredData<Record<string, unknown>>(prompt, { type: 'object' } as Schema, { maxOutputTokens: 8192, temperature: 1.0 });
                return {
                    success: true,
                    data: {
                        status: "DraftReady",
                        officialSubmission: false,
                        iswc: null,
                        ...response
                    }
                };
            } catch (error) {
                const appException = AutonomousIntelligence.handleError(error);
                logger.warn('[PublishingAgent] Registration draft validation failed', appException);
                return {
                    success: false,
                    error: 'Publishing registration could not be validated. No PRO submission or ISWC was generated.',
                    data: {
                        status: "ValidationFailed",
                        officialSubmission: false,
                        iswc: null
                    }
                };
            }
        },
        analyze_contract: async (_args: { file_data: string, mime_type: string }) => {
            const prompt = `Analyze this publishing contract for fair royalty rates and reversion clauses. Return a summary.`;
            const summary = await AutonomousIntelligence.generateText(prompt, { maxOutputTokens: 8192, temperature: 1.0 });
            return { success: true, data: { summary } };
        },
        package_release_assets: async (args: { releaseId: string, assets: Record<string, unknown> }) => {
            const prompt = `Prepare release delivery metadata readiness for release ${args.releaseId}. Assets: ${JSON.stringify(args.assets)}. Return missing delivery fields and review blockers. Do not claim external DSP delivery.`;
            const response = await AutonomousIntelligence.generateStructuredData<Record<string, unknown>>(prompt, { type: 'object' } as Schema, { maxOutputTokens: 8192, temperature: 1.0 });
            return { success: true, data: { status: "ReadyForReview", externalDelivery: false, ...response } };
        },
        check_pro_catalog: PublishingTools.check_pro_catalog,
        search_pro_database: PublishingTools.search_pro_database,
        register_work_with_pro: PublishingTools.register_work_with_pro,
        pro_scraper: UniversalTools.pro_scraper,
        payment_gate: UniversalTools.payment_gate,
        catalog_query_gaps: CatalogAdminTools.catalog_query_gaps,
        catalog_stage_registration_payload: CatalogAdminTools.catalog_stage_registration_payload,
        catalog_dispatch_split_invitations: CatalogAdminTools.catalog_dispatch_split_invitations,
    },
    authorizedTools: ['list_domain_records', 'analyze_contract', 'register_work', 'check_pro_catalog', 'package_release_assets', 'pro_scraper', 'payment_gate', 'search_pro_database', 'register_work_with_pro', 'catalog_query_gaps', 'catalog_stage_registration_payload', 'catalog_dispatch_split_invitations'],
    tools: [{
        functionDeclarations: [
            ...publishingRetrievalDeclarations,
            {
                name: "catalog_query_gaps",
                description: "Query the administrative task queue: audit gaps, staged registration drafts, and outstanding split invitations for the artist's catalog.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        entityType: { type: "STRING", enum: ["master", "track", "release", "composition", "collaborator"] },
                        severity: { type: "STRING", enum: ["info", "warning", "critical", "blocking"] },
                        status: { type: "STRING", enum: ["open", "action_ready", "awaiting_confirmation", "executed", "dismissed", "failed"] },
                        limit: { type: "NUMBER", description: "Max tasks to return (1-100, default 25)" }
                    },
                    required: []
                }
            },
            {
                name: "catalog_stage_registration_payload",
                description: "Build and STAGE a pre-filled registration payload (ISWC/CWR/DDEX_ERN) from a stored catalog record. Draft only — requires human confirmation to execute; nothing is submitted.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        registry: { type: "STRING", enum: ["ISWC", "CWR", "DDEX_ERN"] },
                        releaseId: { type: "STRING" },
                        trackId: { type: "STRING" },
                        masterHash: { type: "STRING" }
                    },
                    required: ["registry"]
                }
            },
            {
                name: "catalog_dispatch_split_invitations",
                description: "Stage split-sheet signature invitations for all collaborators on a release. Idempotent per collaborator+sheet; requires human confirmation before anything is sent.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        releaseId: { type: "STRING" },
                        channel: { type: "STRING", enum: ["in_app", "email"] }
                    },
                    required: ["releaseId"]
                }
            },
            {
                name: "analyze_contract",
                description: "Analyze a publishing contract.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        file_data: { type: "STRING", description: "Base64 file data." },
                        mime_type: { type: "STRING", description: "Mime type (application/pdf)." }
                    },
                    required: ["file_data", "mime_type"]
                }
            },
            {
                name: "register_work",
                description: "Register a new musical work with PROs.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        title: { type: "STRING", description: "Title of the work." },
                        writers: { type: "ARRAY", description: "List of writers.", items: { type: "STRING" } },
                        split: { type: "STRING", description: "Ownership split (e.g. 50/50)." }
                    },
                    required: ["title", "writers"]
                }
            },
            {
                name: "check_pro_catalog",
                description: "Queries PROs (ASCAP/BMI) for existing catalog matches to prevent duplicate registration.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        trackTitle: { type: "STRING", description: "Title of the musical work." },
                        writerName: { type: "STRING", description: "Name of the writer to check." },
                        ipiNumber: { type: "STRING", description: "The IPI (Interested Party Information) number of the writer (optional)." }
                    },
                    required: ["trackTitle", "writerName"]
                }
            },
            {
                name: "package_release_assets",
                description: "Prepare audio, artwork, and metadata for distribution delivery review.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        releaseId: { type: "STRING", description: "The ID of the release record." },
                        assets: { type: "OBJECT", description: "The asset URLs and metadata." }
                    },
                    required: ["releaseId", "assets"]
                }
            },
            {
                name: "pro_scraper",
                description: "Audit public repertories (ASCAP/BMI) for catalog accuracy.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        query: { type: "STRING", description: "Song or Writer name." },
                        society: { type: "STRING", description: "ASCAP or BMI." }
                    },
                    required: ["query", "society"]
                }
            },
            {
                name: "payment_gate",
                description: "Authorize fees for song registration.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        amount: { type: "NUMBER" },
                        vendor: { type: "STRING" },
                        reason: { type: "STRING" }
                    },
                    required: ["amount", "vendor", "reason"]
                }
            },
            {
                name: "search_pro_database",
                description: "Search Performance Rights Organization (PRO) databases for existing catalog matches.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        query: { type: "STRING", description: "Title of the musical work or writer name." },
                        society: { type: "STRING", description: "ASCAP, BMI, or SESAC." }
                    },
                    required: ["query"]
                }
            },
            {
                name: "register_work_with_pro",
                description: "Prepare a draft work-registration packet for a PRO (ASCAP/BMI/SESAC). indii has no direct filing integration with any PRO — this stores the draft and returns a requires_manual_submission status; the user must file it themselves on the PRO's own portal.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        workTitle: { type: "STRING", description: "Title of the work." },
                        writers: {
                            type: "ARRAY",
                            description: "List of writers and their splits.",
                            items: {
                                type: "OBJECT",
                                properties: {
                                    name: { type: "STRING" },
                                    ipi: { type: "STRING" },
                                    role: { type: "STRING" },
                                    split: { type: "NUMBER" }
                                },
                                required: ["name", "role", "split"]
                            }
                        },
                        publisher: {
                            type: "OBJECT",
                            description: "Publisher details and split.",
                            properties: {
                                name: { type: "STRING" },
                                ipi: { type: "STRING" },
                                split: { type: "NUMBER" }
                            },
                            required: ["name", "split"]
                        },
                        society: { type: "STRING", description: "ASCAP, BMI, or SESAC." }
                    },
                    required: ["workTitle", "writers", "society"]
                }
            }
        ]
    }]
};
